document.addEventListener("DOMContentLoaded", () => {
  initFacetAccordion();
  initExternalLinks();
  focusFormStatus();
  focusContactFormError();
  initMobileNavigationAccessibility();
  initMobileMenuButtons();
  initBackToTop();
  initHeaderNavigation();
  handleMobileMenuToggle();
  initQuantityChangeAnnouncements();
});
function initQuantityChangeAnnouncements() {
  // Prevent double initialisation (duplicate listeners cancel out announcements)
  if (window.__qtyAnnounceInit) return;
  window.__qtyAnnounceInit = true;

  const POLL_INTERVAL = 50;   // ms between value checks
  const POLL_TIMEOUT = 1500;  // ms to wait for the value to change

  // 1. Create the live region up front so screen readers have already registered it
  const liveRegion = document.createElement('div');
  liveRegion.className = 'visually-hidden';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  document.body.append(liveRegion);

  let clearTimer;
  const say = (message) => {
    clearTimeout(clearTimer);
    liveRegion.textContent = '';               // reset so identical messages are re-announced
    setTimeout(() => {
      liveRegion.textContent = message;
      clearTimer = setTimeout(() => (liveRegion.textContent = ''), 5000);
    }, 100);
  };

  // 2. Build an accessible name for the quantity field
  const getQuantityLabel = (input) => {
    const ariaLabel = input.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel.trim();

    const labelledBy = input.getAttribute('aria-labelledby');
    if (labelledBy) {
      const text = labelledBy
        .split(/\s+/)
        .map((id) => document.getElementById(id)?.textContent.trim())
        .filter(Boolean)
        .join(' ');
      if (text) return text;
    }

    // Fall back to the product name from the visually-hidden button text
    const hidden = input
      .closest('quantity-input')
      ?.querySelector('.quantity__button .visually-hidden')
      ?.textContent.trim();
    const product = hidden?.replace(/^(Increase|Decrease) quantity for\s*/i, '');
    return product ? `Quantity for ${product}` : 'Quantity';
  };

  // 3. Re-find the input if the theme re-rendered it (cart drawer / section refresh)
  const resolveInput = (input) => {
    if (input.isConnected) return input;
    return input.id ? document.getElementById(input.id) : null;
  };

  // 4. Click on +/- : wait for the value to change, then announce
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button.quantity__button');
      if (!button || button.disabled) return;

      const wrapper = button.closest('quantity-input');
      const input = wrapper?.querySelector('input[type="number"]');
      if (!input) return;

      const direction = button.getAttribute('name'); // "plus" | "minus"
      const previousValue = Number(input.value);
      const label = getQuantityLabel(input);
      const startedAt = Date.now();

      const check = () => {
        const current = resolveInput(input);

        // Input was removed (item deleted or cart re-rendered)
        if (!current) {
          if (direction === 'minus') say(`${label.replace(/^Quantity for\s*/i, '')} removed from cart`);
          return;
        }

        const currentValue = Number(current.value);
        if (Number.isFinite(currentValue) && currentValue !== previousValue) {
          input.dataset.qtyAnnounced = 'true';
          say(`${label}: ${currentValue}`);
          return;
        }

        if (Date.now() - startedAt < POLL_TIMEOUT) {
          setTimeout(check, POLL_INTERVAL);
          return;
        }

        // Timed out with no change: tell the user why
        say(
          direction === 'plus'
            ? `${label}: ${currentValue}. Maximum quantity reached`
            : `${label}: ${currentValue}. Minimum quantity reached`
        );
      };

      setTimeout(check, POLL_INTERVAL);
    },
    true
  );

  // 5. User types a number directly (no button click)
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('quantity-input input[type="number"]')) return;

    // Skip if the click handler above already announced this change
    if (input.dataset.qtyAnnounced === 'true') {
      delete input.dataset.qtyAnnounced;
      return;
    }
    say(`${getQuantityLabel(input)}: ${input.value}`);
  });
}

initQuantityChangeAnnouncements();
function initFacetAccordion() {
  const filters = document.querySelectorAll(
    ".facets__disclosure, .mobile-facets__details"
  );

  filters.forEach((filter) => {
    filter.addEventListener("toggle", () => {
      if (!filter.open) return;

      filters.forEach((item) => {
        if (item !== filter) item.open = false;
      });
    });
  });
}
function initExternalLinks() {
  const links = document.querySelectorAll("a[href]");

  links.forEach((link) => {
    try {
      const href = link.getAttribute("href");
      if (!href) return;

      if (
        href.startsWith("#") ||
        href.startsWith("/") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("sms:")
      ) {
        return;
      }

      const url = new URL(href, window.location.origin);

      if (url.hostname !== window.location.hostname) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");

        const label = link.getAttribute("aria-label") || link.textContent.trim();
        link.setAttribute("aria-label", `${label}, opens in a new tab`);
      }
    } catch (err) {
      console.error("initExternalLinks: skipped a link due to error", link, err);
    }
  });
}
 
function focusContactFormError() {
  const form = document.getElementById("ContactForm");
  const target = form?.querySelector("[data-form-status][role='alert']");

  if (!target) return;

  let attempts = 0;
  const maxAttempts = 6;
  const focusInterval = window.setInterval(() => {
    attempts += 1;

    if (!target.isConnected) {
      window.clearInterval(focusInterval);
      return;
    }

    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: "smooth", block: "center" });

    if (document.activeElement === target || attempts >= maxAttempts) {
      window.clearInterval(focusInterval);
    }
  }, 100);
}

function focusFormStatus() {
  const target = document.querySelector(
    "[data-form-status][role='status']:not([data-form-status-top])"
  );

  if (!target) return;

  const skipLinks = document.querySelector(".skip-links");
  const announcement = target.cloneNode(true);
  announcement.id = `${target.id || "form-status"}-top`;
  announcement.classList.add("visually-hidden");
  announcement.setAttribute("data-form-status-top", "true");

  if (skipLinks) {
    skipLinks.parentNode.insertBefore(announcement, skipLinks);
  } else {
    document.body.insertBefore(announcement, document.body.firstChild);
  }

  let attempts = 0;
  const maxAttempts = 6;
  const focusInterval = window.setInterval(() => {
    attempts += 1;

    if (!announcement.isConnected) {
      window.clearInterval(focusInterval);
      return;
    }

    announcement.focus({ preventScroll: true });

    if (document.activeElement === announcement || attempts >= maxAttempts) {
      window.clearInterval(focusInterval);
    }
  }, 100);
}
 

function initMobileNavigationAccessibility() {
    const menu = document.getElementById("et-mobile-navigation");
    const trigger = document.querySelector(".et-mobile-bar");

    if (!menu || !trigger) return;

    const focusableSelector =
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    let previousState = null;

    function updateMenuState() {
        const isOpen = menu.classList.contains("active");

        if (isOpen === previousState) return;

        previousState = isOpen;

        if (isOpen) {
            // Menu opened
            menu.removeAttribute("aria-hidden");
            menu.removeAttribute("inert");

            const firstFocusable = menu.querySelector(focusableSelector);

            if (firstFocusable) {
                firstFocusable.focus();
            }
        } else {
            // Menu closed
            menu.setAttribute("aria-hidden", "true");
            menu.setAttribute("inert", "");

            trigger.focus();
        }
    }

    // Set the correct state immediately on page load
    updateMenuState();

    // Watch for active class changes
    const observer = new MutationObserver(updateMenuState);

    observer.observe(menu, {
        attributes: true,
        attributeFilter: ["class"]
    });
}
function initMobileMenuButtons() {
  const menu = document.getElementById("et-mobile-navigation");

  if (!menu) return;

  menu.querySelectorAll("a.mm-next-level").forEach((link) => {
    link.setAttribute("role", "button");
    link.setAttribute("aria-expanded", "false");

    const target = link.dataset.target?.replace("#", "");
    if (target) {
      link.setAttribute("aria-controls", target);
    }

    link.addEventListener("click", handleMobileMenuToggle);
  });

  menu.querySelectorAll("a.mm-prev-level").forEach((link) => {
    link.addEventListener("click", () => {
      const currentTrigger = menu.querySelector("a.mm-next-level[aria-expanded='true']");

      if (currentTrigger) {
        currentTrigger.setAttribute("aria-expanded", "false");
        setTimeout(() => currentTrigger.focus({ preventScroll: true }), 80);
      }
    });
  });
}

function focusFirstVisibleItemInPanel(panel) {
  if (!panel) return;

  const focusable = panel.querySelector(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  if (focusable) {
    focusable.focus({ preventScroll: true });
  }
}

function handleMobileMenuToggle(event) {
  const trigger = event.currentTarget;

  // Collapse any previously expanded item
  document
    .querySelectorAll("#et-mobile-navigation a.mm-next-level[aria-expanded='true']")
    .forEach((item) => {
      if (item !== trigger) {
        item.setAttribute("aria-expanded", "false");
      }
    });

  trigger.setAttribute("aria-expanded", "true");

  const targetId = trigger.dataset.target?.replace("#", "");
  if (!targetId) return;

  const targetPanel = document.getElementById(targetId);
  setTimeout(() => focusFirstVisibleItemInPanel(targetPanel), 120);
}
 function initBackToTop() {
  const backToTop = document.getElementById('back-to-top');

  if (!backToTop) {
    return;
  }

  const toggleBackToTop = function () {
    if (window.scrollY > 500) {
      backToTop.classList.add('is-visible');
      backToTop.setAttribute('aria-hidden', 'false');
    } else {
      backToTop.classList.remove('is-visible');
      backToTop.setAttribute('aria-hidden', 'true');
    }
  };

  const scrollToTop = function () {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    /*
     * Move keyboard and screen reader focus
     * to the skip-to-content target.
     */
    const skipToContent = document.getElementById('skip-to-content');

    if (skipToContent) {
      if (!skipToContent.hasAttribute('tabindex')) {
        skipToContent.setAttribute('tabindex', '-1');
      }

      skipToContent.focus({ preventScroll: true });
    }
  };

  window.addEventListener('scroll', toggleBackToTop, { passive: true });

  backToTop.addEventListener('click', scrollToTop);

  toggleBackToTop();
}
 
function initHeaderNavigation() {
  const menuButtons = document.querySelectorAll(
    '.et-header-navigation > li > button[aria-expanded]'
  );

  menuButtons.forEach(function (button) {
    const parentItem = button.closest('.et-header-navigation__item');

    button.addEventListener('click', function (event) {
      event.preventDefault();

      const dropdown = parentItem.querySelector(
        ':scope > .et-header-child-menu, :scope > .et-header-mega-menu-wrap'
      );

      if (!dropdown) return;

      const isExpanded = button.getAttribute('aria-expanded') === 'true';


      // Close other menus
      menuButtons.forEach(function (otherButton) {

        if (otherButton !== button) {

          otherButton.setAttribute('aria-expanded', 'false');

          const otherDropdown = otherButton
            .closest('.et-header-navigation__item')
            .querySelector(
              ':scope > .et-header-child-menu, :scope > .et-header-mega-menu-wrap'
            );

          if (otherDropdown) {
            otherDropdown.classList.remove('is-open');
          }
        }
      });


      // Toggle current menu
      button.setAttribute('aria-expanded', String(!isExpanded));

      dropdown.classList.toggle('is-open', !isExpanded);

    });


    parentItem.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || button.getAttribute('aria-expanded') !== 'true') return;

      event.preventDefault();
      const dropdown = parentItem.querySelector(
        ':scope > .et-header-child-menu, :scope > .et-header-mega-menu-wrap'
      );

      button.setAttribute('aria-expanded', 'false');

      if (dropdown) {
        dropdown.classList.remove('is-open');
      }

      button.focus();
    });

  });
}
// 
(function () {
  var toggle = document.getElementById('MobileSearchToggle');
  var searchForm = document.getElementById('MobileSearchForm');
  if (!toggle || !searchForm) return;

  var input = searchForm.querySelector('input[type="search"]');

  function openSearch() {
    searchForm.classList.add('is-active');
    searchForm.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    // Move focus into the field once it's visible
    if (input) {
      window.requestAnimationFrame(function () {
        input.focus();
      });
    }
  }

  function closeSearch() {
    searchForm.classList.remove('is-active');
    searchForm.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', function () {
    var isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      closeSearch();
      toggle.focus();
    } else {
      openSearch();
    }
  });

  // Close on outside click
  document.addEventListener('click', function (e) {
    var isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (!isOpen) return;
    if (!searchForm.contains(e.target) && e.target !== toggle && !toggle.contains(e.target)) {
      closeSearch();
    }
  });

  // Close on Escape, return focus to toggle
  searchForm.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeSearch();
      toggle.focus();
    }
  });
})();